import React, { useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@jem-open/jem-ui';
import { AlertTriangle, Plus } from 'lucide-react';
import {
  isReconValid,
  MultiProductReconProvider,
  RECON_PRODUCTS,
  ReconGroup,
  useMultiProductRecon,
} from './MultiProductReconContext';
import PayCycleSelector from './components/PayCycleSelector';
import ReconGroupForm from './components/ReconGroupForm';
import RecurrenceForm from './components/RecurrenceForm';
import InvoicingSection from './components/InvoicingSection';
import CalendarDetailsForm from './components/CalendarDetailsForm';
import EmailRecipientsSection from './components/EmailRecipientsSection';
import ConfirmStep from './components/ConfirmStep';
import CreateMultiProductReconActions, {
  ModalStep,
} from './components/CreateMultiProductReconActions';

export interface PayCycle {
  id: number;
  name: string;
  frequency: string;
  frequency_type: string;
}

interface CreateMultiProductReconModalProps {
  isOpen: boolean;
  onClose: () => void;
  employerName: string;
  employerId: string;
  payCycles: PayCycle[];
}

const sectionHeading =
  'text-[18px] font-semibold text-[--greyscale-text-title]';

const reconSummary = (recon: ReconGroup, index: number): string => {
  const label = `Recon ${index + 1}`;
  if (recon.products.length === 0) return `${label} - no products selected`;
  const productLabels = recon.products
    .map((p) => RECON_PRODUCTS.find((rp) => rp.value === p)?.label ?? p)
    .join(' + ');
  return `${label} - ${productLabels}`;
};

interface ConfigureStepProps {
  employerId: string;
  employerName: string;
  payCycles: PayCycle[];
}

const ConfigureStep = ({
  employerId,
  employerName,
  payCycles,
}: ConfigureStepProps) => {
  const { state, addRecon } = useMultiProductRecon();
  const [activeReconId, setActiveReconId] = useState<string>(
    state.recons[0]?.id ?? '',
  );

  const handleAddRecon = () => {
    const newId = addRecon();
    setActiveReconId(newId);
  };

  // Each product can belong to at most one recon, so once every product
  // is taken an additional recon could never become valid.
  const allProductsTaken =
    new Set(state.recons.flatMap((r) => r.products)).size >=
    RECON_PRODUCTS.length;

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <h3 className={sectionHeading}>Pay Cycles</h3>
        <PayCycleSelector payCycles={payCycles} />
      </section>

      <section className="space-y-3">
        <h3 className={sectionHeading}>Recons</h3>
        <TooltipProvider>
          <Accordion
            type="single"
            collapsible
            value={activeReconId}
            onValueChange={setActiveReconId}
            className="space-y-3"
          >
            {state.recons.map((recon, index) => {
              const valid = isReconValid(recon);
              return (
                <AccordionItem
                  key={recon.id}
                  value={recon.id}
                  className="overflow-hidden rounded-md border bg-slate-50 [&[data-state=open]]:bg-white"
                >
                  <AccordionTrigger
                    className="!rounded-none bg-slate-50 px-3 py-3 !text-[--greyscale-text-title] hover:!text-[--pink-900] [&[data-state=open]]:!bg-[--pink-50]"
                  >
                    <span className="flex items-center gap-2">
                      {!valid && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertTriangle
                              className="size-4 text-yellow-600"
                              aria-label={`Recon ${index + 1} is incomplete`}
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            This recon has missing required fields
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <span>{reconSummary(recon, index)}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3 data-[state=closed]:hidden">
                    <ReconGroupForm
                      reconId={recon.id}
                      isOnlyRecon={state.recons.length === 1}
                    />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          {allProductsTaken ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block">
                  <Button
                    variant="outline"
                    className="w-full"
                    leftIcon={<Plus className="size-4" />}
                    disabled
                  >
                    Add another recon
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                All products are already covered by the recons above
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              variant="outline"
              className="w-full"
              leftIcon={<Plus className="size-4" />}
              onClick={handleAddRecon}
            >
              Add another recon
            </Button>
          )}
        </TooltipProvider>
      </section>

      <section className="space-y-2">
        <h3 className={sectionHeading}>Schedule</h3>
        <RecurrenceForm />
      </section>

      <section className="space-y-2">
        <h3 className={sectionHeading}>Invoicing</h3>
        <InvoicingSection />
      </section>

      <section className="space-y-2">
        <h3 className={sectionHeading}>Calendar event</h3>
        <CalendarDetailsForm employerName={employerName} />
      </section>

      <section className="space-y-2">
        <h3 className={sectionHeading}>Email recipients</h3>
        <EmailRecipientsSection employerId={employerId} />
      </section>
    </div>
  );
};

const STEP_LABEL: Record<ModalStep, string> = {
  configure: 'Step 1 of 2 - Configure',
  review: 'Step 2 of 2 - Confirm',
};

const CreateMultiProductReconModal = ({
  isOpen,
  onClose,
  employerId,
  employerName,
  payCycles,
}: CreateMultiProductReconModalProps) => {
  const [step, setStep] = useState<ModalStep>('configure');

  const handleClose = () => {
    setStep('configure');
    onClose();
  };

  return (
    <MultiProductReconProvider>
      <Modal isOpen={isOpen} size="lg" toggle={handleClose}>
        <ModalHeader toggle={handleClose}>
          <div className="flex flex-col gap-1">
            <span style={{ fontSize: 24 }}>Create a Multi-Product Recon</span>
            <span className="text-xs font-normal text-muted-foreground">
              {STEP_LABEL[step]}
            </span>
          </div>
        </ModalHeader>
        <ModalBody>
          {step === 'configure' ? (
            <ConfigureStep
              employerId={employerId}
              employerName={employerName}
              payCycles={payCycles}
            />
          ) : (
            <ConfirmStep />
          )}
        </ModalBody>
        <ModalFooter>
          <CreateMultiProductReconActions
            step={step}
            onClose={handleClose}
            onNext={() => setStep('review')}
            onBack={() => setStep('configure')}
            employerId={employerId}
          />
        </ModalFooter>
      </Modal>
    </MultiProductReconProvider>
  );
};

export default CreateMultiProductReconModal;
